import { uid, Scene, Asset, Character, Reference, CanvasNode, CanvasEdge, GenerationJob, AssistantThread, AssistantMessage, Project, ProjectScript } from "@/domain/entities";
import { Connection, syncConnectionsWithCatalog } from "@/domain/connections";
import { canConnect } from "@/domain/canvasRules";
import { buildDemoFixture } from "@/data/fixtures";
import {
  validateHandleCollision,
  validateReferenceAuthorization,
  evaluateGlobalEditPolicy,
  normalizeHandle,
} from "@/domain/librarySelectors";

// Estado de cada paso del onboarding: pending (no decidido) | completed (continuar explícito) | skipped (omitido explícito).
// Haber visitado el paso (navegar a él) nunca cambia este estado por sí solo.
export const emptyStepStatus = () => ({ access: "pending", workspace: "pending", connections: "pending", assistant: "pending", budget: "pending", first: "pending" });

export const emptyOnboarding = () => ({
  status: "not_started", // not_started | in_progress | completed
  step: 0,
  stepStatus: emptyStepStatus(),
  ownerName: "",
  workspaceName: "",
  language: "es",
  workspacePrefs: { format: "16:9", imageResolution: "1K" },
  budget: { monthlyLimit: null, alertPct: 80, blockOnLimit: true },
});

export const buildInitialState = (withDemo = true) => ({
  version: 2,
  onboarding: emptyOnboarding(),
  continuityDecisions: {},
  ...(withDemo ? buildDemoFixture() : { projects: [], scenes: [], assets: [], characters: [], references: [], nodes: [], edges: [], jobs: [], threads: [], usage: [], connections: [], selection: {}, scripts: [] }),
});

const touch = (o) => ({ ...o, updatedAt: new Date().toISOString() });
const byOrder = (a, b) => a.order - b.order;
const reindex = (scenes, projectId) => {
  let i = 0;
  return scenes.sort(byOrder).map((s) => (s.projectId === projectId ? { ...s, order: i++ } : s));
};

export function reducer(state, action) {
  const { type, payload: p = {} } = action;
  switch (type) {
    case "HYDRATE": {
      // Merge server onboarding with defaults so missing fields (e.g. workspacePrefs
      // absent in older DB documents) never leave the state with undefined values.
      const nextOnboarding = p.onboarding
        ? { ...emptyOnboarding(), ...p.onboarding }
        : state.onboarding;

      const rawChars = p.characters || state.characters || [];
      const normalizedCharacters = rawChars.map((c) => Character(c));

      const rawRefs = p.references || state.references || [];
      const normalizedReferences = rawRefs.map((r) => {
        const parentChar = normalizedCharacters.find((c) => c.id === r.characterId);
        const refProjectId = r.projectId || parentChar?.projectId || null;
        return Reference({ ...r, projectId: refProjectId });
      });

      const rawScripts = p.scripts || state.scripts || [];
      const normalizedScripts = rawScripts.map((s) => ProjectScript(s));

      const rawScenes = p.scenes || state.scenes || [];
      const normalizedScenes = rawScenes.map((s) => Scene(s));

      return {
        ...state,
        ...p,
        scenes: normalizedScenes,
        characters: normalizedCharacters,
        references: normalizedReferences,
        scripts: normalizedScripts,
        onboarding: nextOnboarding,
        continuityDecisions: p.continuityDecisions || state.continuityDecisions || {},
      };
    }
    case "RESET_DEMO":
      return { ...buildInitialState(true), onboarding: state.onboarding };
    case "CLEAR_ALL":
      return buildInitialState(false);

    case "ONBOARDING_UPDATE":
      return { ...state, onboarding: { ...state.onboarding, ...p, status: p.status || (state.onboarding.status === "completed" ? "completed" : "in_progress") } };
    case "ONBOARDING_COMPLETE":
      return { ...state, onboarding: { ...state.onboarding, status: "completed" } };

    case "CONNECTION_CREATE":
      return { ...state, connections: [...state.connections, Connection(p)] };
    case "CONNECTION_DELETE": {
      const jobs = state.jobs.map((j) => (j.connectionId === p.id ? { ...j, connectionId: null } : j));
      return { ...state, connections: state.connections.filter((c) => c.id !== p.id), jobs };
    }
    case "CONNECTIONS_SYNC_CATALOG": {
      const nextConnections = syncConnectionsWithCatalog(state.connections || [], p);
      return { ...state, connections: nextConnections };
    }

    case "PROJECT_CREATE": {
      const project = Project(p);
      return { ...state, projects: [project, ...state.projects], threads: [...state.threads, AssistantThread({ projectId: project.id })], selection: { ...state.selection, [project.id]: { sceneId: null, assetId: null, nodeId: null, edgeId: null } } };
    }
    case "PROJECT_UPDATE":
      return { ...state, projects: state.projects.map((x) => (x.id === p.id ? touch({ ...x, ...p.patch }) : x)) };
    case "PROJECT_SET_ACTIVE_PLAN": {
      const { projectId, plan } = p;
      return {
        ...state,
        projects: state.projects.map((proj) => {
          if (proj.id !== projectId) return proj;
          return touch({
            ...proj,
            activePlan: {
              ...plan,
              status: "active",
              activatedAt: new Date().toISOString(),
            },
          });
        }),
      };
    }
    case "PROJECT_CLEAR_ACTIVE_PLAN": {
      const { projectId } = p;
      return {
        ...state,
        projects: state.projects.map((proj) => {
          if (proj.id !== projectId) return proj;
          return touch({
            ...proj,
            activePlan: null,
          });
        }),
      };
    }
    case "PROJECT_DELETE": {
      const gone = (x) => x.projectId !== p.id;
      const { [p.id]: _drop, ...selection } = state.selection;
      return {
        ...state,
        projects: state.projects.filter((x) => x.id !== p.id),
        scenes: state.scenes.filter(gone),
        assets: state.assets.filter(gone),
        characters: state.characters.filter(gone),
        references: state.references.filter(gone),
        nodes: state.nodes.filter(gone),
        edges: state.edges.filter(gone),
        jobs: state.jobs.filter(gone),
        threads: state.threads.filter(gone),
        scripts: (state.scripts || []).filter(gone),
        selection,
      };
    }
    case "PROJECT_LINK_RESOURCE": {
      const { projectId, resourceId } = p;
      const project = state.projects.find((pr) => pr.id === projectId);
      if (!project) return state;
      const targetChar = state.characters.find((c) => c.id === resourceId);
      const targetRef = state.references.find((r) => r.id === resourceId);
      const target = targetChar || targetRef;
      if (!target || target.scope !== "global") {
        return {
          ...state,
          lastError: {
            at: Date.now(),
            code: "LINK_INVALID",
            message: "Solo pueden vincularse recursos de la biblioteca global.",
          },
        };
      }
      if (targetChar) {
        const collision = validateHandleCollision(state, {
          handle: targetChar.handle,
          projectId,
          scope: "project",
        });
        if (!collision.valid) {
          return {
            ...state,
            lastError: { at: Date.now(), code: "HANDLE_COLLISION", message: collision.reason },
          };
        }
      }
      const existing = project.linkedResourceIds || [];
      if (existing.includes(resourceId)) return state;
      return {
        ...state,
        projects: state.projects.map((pr) =>
          pr.id === projectId ? touch({ ...pr, linkedResourceIds: [...existing, resourceId] }) : pr
        ),
        lastError: null,
      };
    }
    case "PROJECT_UNLINK_RESOURCE": {
      const { projectId, resourceId } = p;
      const project = state.projects.find((pr) => pr.id === projectId);
      if (!project) return state;
      const existing = project.linkedResourceIds || [];
      const updatedLinks = existing.filter((id) => id !== resourceId);
      const targetChar = state.characters.find((c) => c.id === resourceId);
      const relatedRefIds = targetChar
        ? state.references.filter((r) => r.characterId === targetChar.id).map((r) => r.id)
        : [resourceId];
      const updatedScenes = state.scenes.map((s) => {
        if (s.projectId !== projectId) return s;
        const remainingRefs = (s.referenceIds || []).filter((id) => !relatedRefIds.includes(id));
        return remainingRefs.length === (s.referenceIds || []).length
          ? s
          : touch({ ...s, referenceIds: remainingRefs });
      });
      return {
        ...state,
        projects: state.projects.map((pr) =>
          pr.id === projectId ? touch({ ...pr, linkedResourceIds: updatedLinks }) : pr
        ),
        scenes: updatedScenes,
      };
    }

    case "SCENE_CREATE": {
      // Validación estricta de autorización de recursos vinculados (Fase 2B.4B)
      if (Array.isArray(p.referenceIds) && p.referenceIds.length > 0) {
        for (const refId of p.referenceIds) {
          const auth = validateReferenceAuthorization(state, {
            referenceId: refId,
            projectId: p.projectId,
          });
          if (!auth.authorized) {
            return {
              ...state,
              lastError: {
                at: Date.now(),
                code: "UNAUTHORIZED_REFERENCE",
                message: auth.reason,
              },
            };
          }
        }
      }

      const order = state.scenes.filter((s) => s.projectId === p.projectId).length;
      const scene = Scene({ ...p, order });
      const node = p.withNode === false ? null : CanvasNode({ projectId: p.projectId, type: "image", x: 420 + (order % 3) * 40, y: 120 + order * 150, data: { sceneId: scene.id } });
      return { ...state, scenes: [...state.scenes, scene], nodes: node ? [...state.nodes, node] : state.nodes, selection: { ...state.selection, [p.projectId]: { ...(state.selection[p.projectId] || {}), sceneId: scene.id, assetId: scene.selectedAssetId, nodeId: node?.id || null } }, lastError: null };
    }
    case "SCENE_UPDATE":
      return { ...state, scenes: state.scenes.map((s) => (s.id === p.id ? touch({ ...s, ...p.patch }) : s)) };
    case "SCENE_DELETE": {
      const scene = state.scenes.find((s) => s.id === p.id);
      if (!scene) return state;
      const nodeIds = state.nodes.filter((n) => n.data?.sceneId === p.id).map((n) => n.id);
      const sel = state.selection[scene.projectId] || {};
      return {
        ...state,
        scenes: reindex(state.scenes.filter((s) => s.id !== p.id), scene.projectId),
        nodes: state.nodes.filter((n) => !nodeIds.includes(n.id)),
        edges: state.edges.filter((e) => !nodeIds.includes(e.sourceId) && !nodeIds.includes(e.targetId)),
        selection: { ...state.selection, [scene.projectId]: { ...sel, sceneId: sel.sceneId === p.id ? null : sel.sceneId, nodeId: nodeIds.includes(sel.nodeId) ? null : sel.nodeId } },
      };
    }
    case "SCENE_RESTORE": {
      const scene = p.scene;
      if (!scene || !scene.id || !scene.projectId) return state;
      // Impide duplicados si ya existe en las escenas activas
      if (state.scenes.some((s) => s.id === scene.id)) {
        return state;
      }
      const projectScenes = state.scenes
        .filter((s) => s.projectId === scene.projectId)
        .sort(byOrder);
      const targetPos = typeof p.originalOrder === "number" ? p.originalOrder : projectScenes.length;
      const insertIdx = Math.max(0, Math.min(targetPos, projectScenes.length));
      const nextProjectScenes = [
        ...projectScenes.slice(0, insertIdx),
        touch({ ...scene }),
        ...projectScenes.slice(insertIdx),
      ].map((s, idx) => ({ ...s, order: idx }));

      const existingNodeIds = new Set(state.nodes.map((n) => n.id));
      const restoredNodes = (p.nodes || []).filter((n) => !existingNodeIds.has(n.id));
      const nextNodes = restoredNodes.length > 0 ? [...state.nodes, ...restoredNodes] : state.nodes;

      const existingEdgeIds = new Set(state.edges.map((e) => e.id));
      const restoredEdges = (p.edges || []).filter((e) => !existingEdgeIds.has(e.id));
      const nextEdges = restoredEdges.length > 0 ? [...state.edges, ...restoredEdges] : state.edges;

      const sel = state.selection[scene.projectId] || {};
      return {
        ...state,
        scenes: [
          ...state.scenes.filter((s) => s.projectId !== scene.projectId),
          ...nextProjectScenes,
        ],
        nodes: nextNodes,
        edges: nextEdges,
        selection: {
          ...state.selection,
          [scene.projectId]: {
            ...sel,
            sceneId: scene.id,
            assetId: scene.selectedAssetId || sel.assetId,
          },
        },
      };
    }
    case "SCENE_MOVE": {
      const scene = state.scenes.find((s) => s.id === p.id);
      if (!scene) return state;
      const list = state.scenes.filter((s) => s.projectId === scene.projectId).sort(byOrder);
      const idx = list.findIndex((s) => s.id === p.id);
      const j = idx + p.dir;
      if (j < 0 || j >= list.length) return state;
      const swapped = list.map((s, i) => (i === idx ? { ...s, order: j } : i === j ? { ...s, order: idx } : s));
      return { ...state, scenes: [...state.scenes.filter((s) => s.projectId !== scene.projectId), ...swapped] };
    }
    case "SCENE_DUPLICATE": {
      const src = state.scenes.find((s) => s.id === p.id);
      if (!src) return state;
      const list = state.scenes.filter((s) => s.projectId === src.projectId).sort(byOrder);
      const copy = Scene({ ...src, id: undefined, title: `${src.title} (copia)`, order: src.order + 1, isDemo: false, createdAt: undefined, updatedAt: undefined });
      const shifted = list.map((s) => (s.order > src.order ? { ...s, order: s.order + 1 } : s));
      const node = CanvasNode({ projectId: src.projectId, type: "image", x: 460, y: 160 + copy.order * 150, data: { sceneId: copy.id } });
      return { ...state, scenes: [...state.scenes.filter((s) => s.projectId !== src.projectId), ...shifted, copy], nodes: [...state.nodes, node], selection: { ...state.selection, [src.projectId]: { ...(state.selection[src.projectId] || {}), sceneId: copy.id, assetId: copy.selectedAssetId, nodeId: node.id } } };
    }
    case "SCENE_TOGGLE_REFERENCE": {
      const scene = state.scenes.find((s) => s.id === p.sceneId);
      if (!scene) return state;
      const ref = state.references.find((r) => r.id === p.referenceId);
      if (!ref) return state;

      const isAdding = !(scene.referenceIds || []).includes(p.referenceId);
      if (isAdding) {
        const auth = validateReferenceAuthorization(state, {
          referenceId: p.referenceId,
          projectId: scene.projectId,
        });
        if (!auth.authorized) {
          return {
            ...state,
            lastError: {
              at: Date.now(),
              code: "UNAUTHORIZED_REFERENCE",
              message: auth.reason,
            },
          };
        }
      }

      return {
        ...state,
        scenes: state.scenes.map((s) =>
          s.id !== p.sceneId
            ? s
            : touch({
                ...s,
                referenceIds: (s.referenceIds || []).includes(p.referenceId)
                  ? s.referenceIds.filter((r) => r !== p.referenceId)
                  : [...(s.referenceIds || []), p.referenceId],
              })
        ),
        lastError: null,
      };
    }
    case "SCENE_SELECT_ASSET": {
      const scene = state.scenes.find((s) => s.id === p.sceneId);
      if (!scene) return state;

      const currentAssetIds = scene.assetIds || [];
      const updatedAssetIds = currentAssetIds.includes(p.assetId)
        ? currentAssetIds
        : [...currentAssetIds, p.assetId];

      const targetAsset = state.assets.find((a) => a.id === p.assetId);
      const updatedAssets = targetAsset && !targetAsset.projectId && !targetAsset.isDemo
        ? state.assets.map((a) => (a.id === p.assetId ? touch({ ...a, projectId: scene.projectId }) : a))
        : state.assets;

      return {
        ...state,
        assets: updatedAssets,
        scenes: state.scenes.map((s) =>
          s.id === p.sceneId
            ? touch({
                ...s,
                selectedAssetId: p.assetId,
                assetIds: updatedAssetIds,
              })
            : s
        ),
        selection: {
          ...state.selection,
          [scene.projectId]: {
            ...(state.selection[scene.projectId] || {}),
            sceneId: p.sceneId,
            assetId: p.assetId,
          },
        },
        lastError: null,
      };
    }


    case "ASSET_ADD": {
      const asset = Asset(p);
      const scenes = asset.sceneId ? state.scenes.map((s) => (s.id === asset.sceneId ? touch({ ...s, assetIds: [...s.assetIds, asset.id], selectedAssetId: asset.id }) : s)) : state.scenes;
      const selection = asset.projectId ? { ...state.selection, [asset.projectId]: { ...(state.selection[asset.projectId] || {}), assetId: asset.id } } : state.selection;
      return { ...state, assets: [...state.assets, asset], scenes, selection };
    }

    case "ASSET_UPDATE": {
      const existing = state.assets.find((a) => a.id === p.id);
      if (!existing) return state;
      const updated = touch({ ...existing, ...p.patch });
      return {
        ...state,
        assets: state.assets.map((a) => (a.id === p.id ? updated : a)),
      };
    }

    case "ASSETS_DELETE": {
      // p.ids: string[] — elimina assets por ID sin tocar derivados/originales relacionados.
      // También limpia las escenas, la selección y los nodos que apuntaban a ellos para
      // que no queden referencias colgantes (biblioteca, Galería y Editor coherentes).
      const delSet = new Set(p.ids || []);
      const scenes = state.scenes.map((s) => {
        const hit = (s.assetIds || []).some((id) => delSet.has(id)) || delSet.has(s.selectedAssetId);
        if (!hit) return s;
        const assetIds = (s.assetIds || []).filter((id) => !delSet.has(id));
        const selectedAssetId = delSet.has(s.selectedAssetId) ? (assetIds[assetIds.length - 1] ?? null) : s.selectedAssetId;
        return touch({ ...s, assetIds, selectedAssetId });
      });
      const selection = Object.fromEntries(Object.entries(state.selection).map(([pid, sel]) => {
        if (!delSet.has(sel.assetId)) return [pid, sel];
        const sc = scenes.find((s) => s.id === sel.sceneId);
        return [pid, { ...sel, assetId: sc?.selectedAssetId ?? null }];
      }));
      const nodes = state.nodes.map((n) => (delSet.has(n.data?.assetId) ? { ...n, data: { ...n.data, assetId: null } } : n));
      return { ...state, assets: state.assets.filter((a) => !delSet.has(a.id)), scenes, selection, nodes };
    }

    case "SCENE_REMOVE_ASSET": {
      // Quita el recurso de la escena SIN eliminar el archivo (sigue en la biblioteca/Galería).
      const scene = state.scenes.find((s) => s.id === p.sceneId);
      if (!scene) return state;
      const assetIds = (scene.assetIds || []).filter((id) => id !== p.assetId);
      const selectedAssetId = scene.selectedAssetId === p.assetId ? (assetIds[assetIds.length - 1] ?? null) : scene.selectedAssetId;
      const sel = state.selection[scene.projectId] || {};
      return {
        ...state,
        scenes: state.scenes.map((s) => (s.id === scene.id ? touch({ ...s, assetIds, selectedAssetId }) : s)),
        assets: state.assets.map((a) => (a.id === p.assetId && a.sceneId === scene.id ? { ...a, sceneId: null } : a)),
        selection: { ...state.selection, [scene.projectId]: { ...sel, assetId: sel.assetId === p.assetId ? selectedAssetId : sel.assetId } },
      };
    }

    case "CHARACTER_CREATE": {
      const targetScope = p.scope || (p.projectId ? "project" : "pending_review");
      const collision = validateHandleCollision(state, {
        handle: p.handle,
        projectId: p.projectId || null,
        scope: targetScope,
      });
      if (!collision.valid) {
        return {
          ...state,
          lastError: { at: Date.now(), code: "HANDLE_COLLISION", message: collision.reason },
        };
      }
      const ch = Character({ ...p, scope: targetScope, projectId: p.projectId || null });
      const ref = Reference({
        kind: ch.kind,
        characterId: ch.id,
        assetId: ch.masterAssetId,
        label: `@${ch.handle}`,
        projectId: ch.projectId,
        scope: ch.scope,
        reviewStatus: ch.reviewStatus,
        isDemo: ch.isDemo,
      });
      return {
        ...state,
        characters: [...state.characters, ch],
        references: [...state.references, ref],
        lastError: null,
      };
    }
    case "CHARACTER_UPDATE": {
      const existing = state.characters.find((c) => c.id === p.id);
      if (!existing) return state;

      if (existing.scope === "project" && p.projectId && p.projectId !== existing.projectId) {
        return {
          ...state,
          lastError: {
            at: Date.now(),
            code: "UNAUTHORIZED_SCOPE",
            message: `No autorizado: el recurso pertenece al proyecto ${existing.projectId}.`,
          },
        };
      }

      const newHandle = p.patch?.handle;
      if (newHandle && normalizeHandle(newHandle) !== normalizeHandle(existing.handle)) {
        const collision = validateHandleCollision(state, {
          handle: newHandle,
          projectId: existing.projectId || p.projectId || null,
          scope: existing.scope,
          excludeId: existing.id,
        });
        if (!collision.valid) {
          return {
            ...state,
            lastError: { at: Date.now(), code: "HANDLE_COLLISION", message: collision.reason },
          };
        }
      }

      if (existing.scope === "global") {
        const policy = evaluateGlobalEditPolicy(state, existing.id, {
          requestedProjectId: p.projectId,
          allowGlobalMutation: p.allowGlobalMutation,
        });

        if (policy.requiresFork && p.projectId) {
          const forkedChar = Character({
            ...existing,
            ...p.patch,
            id: uid("chr"),
            projectId: p.projectId,
            scope: "project",
            reviewStatus: "confirmed",
            version: 1,
            isDemo: false,
          });
          const forkedRef = Reference({
            kind: forkedChar.kind,
            characterId: forkedChar.id,
            assetId: forkedChar.masterAssetId,
            label: `@${forkedChar.handle}`,
            projectId: p.projectId,
            scope: "project",
            reviewStatus: "confirmed",
            isDemo: false,
          });
          const oldRefIds = state.references.filter((r) => r.characterId === existing.id).map((r) => r.id);
          const updatedScenes = state.scenes.map((s) => {
            if (s.projectId !== p.projectId) return s;
            const hasOld = (s.referenceIds || []).some((rId) => oldRefIds.includes(rId));
            if (!hasOld) return s;
            return touch({
              ...s,
              referenceIds: s.referenceIds.map((rId) => (oldRefIds.includes(rId) ? forkedRef.id : rId)),
            });
          });
          const updatedProjects = state.projects.map((pr) => {
            if (pr.id !== p.projectId) return pr;
            const updatedLinks = (pr.linkedResourceIds || []).filter((id) => id !== existing.id && !oldRefIds.includes(id));
            return touch({ ...pr, linkedResourceIds: updatedLinks });
          });
          return {
            ...state,
            characters: [...state.characters, forkedChar],
            references: [...state.references, forkedRef],
            scenes: updatedScenes,
            projects: updatedProjects,
            lastError: null,
          };
        }

        if (!policy.allowed) {
          return {
            ...state,
            lastError: {
              at: Date.now(),
              code: "GLOBAL_MUTATION_RESTRICTED",
              message: policy.reason,
            },
          };
        }
      }

      const updatedChar = touch(
        Character({
          ...existing,
          ...p.patch,
          version: existing.scope === "global" ? (existing.version || 1) + 1 : (existing.version || 1),
        })
      );

      return {
        ...state,
        characters: state.characters.map((c) => (c.id === p.id ? updatedChar : c)),
        references: state.references.map((r) =>
          r.characterId === p.id
            ? {
                ...r,
                label: `@${updatedChar.handle}`,
                assetId: updatedChar.masterAssetId,
                projectId: updatedChar.projectId,
                scope: updatedChar.scope,
                reviewStatus: updatedChar.reviewStatus,
              }
            : r
        ),
        lastError: null,
      };
    }
    case "CHARACTER_DELETE": {
      const existing = state.characters.find((c) => c.id === p.id);
      if (!existing) return state;

      if (existing.scope === "project" && p.projectId && p.projectId !== existing.projectId) {
        return {
          ...state,
          lastError: {
            at: Date.now(),
            code: "UNAUTHORIZED_SCOPE",
            message: `No autorizado: el recurso pertenece al proyecto ${existing.projectId}.`,
          },
        };
      }

      const refIds = state.references.filter((r) => r.characterId === p.id).map((r) => r.id);
      const nodeIds = state.nodes.filter((n) => refIds.includes(n.data?.referenceId)).map((n) => n.id);

      const updatedProjects = state.projects.map((pr) => {
        if (!(pr.linkedResourceIds || []).includes(p.id) && !refIds.some((rId) => (pr.linkedResourceIds || []).includes(rId))) {
          return pr;
        }
        return touch({
          ...pr,
          linkedResourceIds: (pr.linkedResourceIds || []).filter((id) => id !== p.id && !refIds.includes(id)),
        });
      });

      return {
        ...state,
        projects: updatedProjects,
        characters: state.characters.filter((c) => c.id !== p.id),
        references: state.references.filter((r) => r.characterId !== p.id),
        scenes: state.scenes.map((s) => ({
          ...s,
          referenceIds: s.referenceIds.filter((r) => !refIds.includes(r)),
        })),
        nodes: state.nodes.filter((n) => !nodeIds.includes(n.id)),
        edges: state.edges.filter((e) => !nodeIds.includes(e.sourceId) && !nodeIds.includes(e.targetId)),
        lastError: null,
      };
    }
    case "REFERENCE_CREATE": {
      const auth = validateReferenceAuthorization(state, {
        characterId: p.characterId,
        projectId: p.projectId,
      });
      if (!auth.authorized) {
        return {
          ...state,
          lastError: {
            at: Date.now(),
            code: "UNAUTHORIZED_REFERENCE",
            message: auth.reason,
          },
        };
      }
      const ref = Reference(p);
      return {
        ...state,
        references: [...state.references, ref],
        lastError: null,
      };
    }

    case "NODE_ADD": {
      const node = CanvasNode(p);
      return { ...state, nodes: [...state.nodes, node], selection: { ...state.selection, [node.projectId]: { ...(state.selection[node.projectId] || {}), nodeId: node.id, edgeId: null } } };
    }
    case "NODE_MOVE":
      return { ...state, nodes: state.nodes.map((n) => (n.id === p.id ? { ...n, x: p.x, y: p.y } : n)) };
    case "NODE_UPDATE":
      return { ...state, nodes: state.nodes.map((n) => (n.id === p.id ? { ...n, data: { ...n.data, ...p.data } } : n)) };
    case "NODE_DELETE": {
      const node = state.nodes.find((n) => n.id === p.id);
      if (!node) return state;
      const sel = state.selection[node.projectId] || {};
      return { ...state, nodes: state.nodes.filter((n) => n.id !== p.id), edges: state.edges.filter((e) => e.sourceId !== p.id && e.targetId !== p.id), selection: { ...state.selection, [node.projectId]: { ...sel, nodeId: sel.nodeId === p.id ? null : sel.nodeId } } };
    }
    case "EDGE_ADD": {
      const nodes = state.nodes.filter((n) => n.projectId === p.projectId);
      const edges = state.edges.filter((e) => e.projectId === p.projectId);
      const check = canConnect(nodes, edges, p.sourceId, p.targetId);
      if (!check.ok) return { ...state, lastError: { at: Date.now(), message: check.reason } };
      return { ...state, edges: [...state.edges, CanvasEdge(p)], lastError: null };
    }
    case "EDGE_DELETE": {
      const edge = state.edges.find((e) => e.id === p.id);
      if (!edge) return state;
      const sel = state.selection[edge.projectId] || {};
      return { ...state, edges: state.edges.filter((e) => e.id !== p.id), selection: { ...state.selection, [edge.projectId]: { ...sel, edgeId: null } } };
    }
    case "EDGE_UPDATE": {
      const edge = state.edges.find((e) => e.id === p.id);
      if (!edge) return state;
      return {
        ...state,
        edges: state.edges.map((e) => (e.id === p.id ? { ...e, ...p.patch } : e)),
      };
    }
    case "CLEAR_ERROR":
      return { ...state, lastError: null };

    case "JOB_CREATE":
      return { ...state, jobs: [GenerationJob(p), ...state.jobs] };
    case "JOB_REMOVE_BLOCKED":
      return { ...state, jobs: state.jobs.filter((j) => j.status !== "blocked") };
    case "JOB_UPSERT": {
      // Integra una tarea del backend (por job_id) en el estado local.
      // Si existe (por backendJobId), actualiza; si no, inserta al inicio.
      const backendId = p.job_id;
      const exists = state.jobs.some((j) => j.backendJobId === backendId);
      const mapped = _mapBackendJob(p);
      if (exists) {
        return { ...state, jobs: state.jobs.map((j) => j.backendJobId === backendId ? { ...j, ...mapped } : j) };
      }
      return { ...state, jobs: [mapped, ...state.jobs] };
    }

    case "THREAD_MESSAGE": {
      const exists = state.threads.some((th) => th.projectId === p.projectId);
      const threads = exists ? state.threads : [...state.threads, AssistantThread({ projectId: p.projectId })];
      return { ...state, threads: threads.map((th) => (th.projectId === p.projectId ? { ...th, messages: [...th.messages, AssistantMessage(p.message)] } : th)) };
    }
    case "PROPOSAL_APPLIED": {
      const allIndices = Array.isArray(p.appliedIndices) ? p.appliedIndices : null;
      return {
        ...state,
        threads: state.threads.map((th) =>
          th.projectId !== p.projectId
            ? th
            : {
                ...th,
                messages: th.messages.map((m) => {
                  if (p.messageId && m.id !== p.messageId) return m;
                  return {
                    ...m,
                    proposals: (m.proposals || []).map((pr) => {
                      if (pr.id !== p.proposalId) return pr;
                      const finalIndices = allIndices || (pr.scenes || []).map((_, idx) => idx);
                      return { ...pr, applied: true, appliedIndices: finalIndices, dismissed: false };
                    }),
                  };
                }),
              }
        ),
      };
    }

    case "PROPOSAL_SCENE_APPLIED": {
      const { projectId, proposalId, messageId, sceneIndex } = p;
      return {
        ...state,
        threads: state.threads.map((th) => {
          if (th.projectId !== projectId) return th;
          return {
            ...th,
            messages: th.messages.map((m) => {
              if (messageId && m.id !== messageId) return m;
              if (!m.proposals || !m.proposals.some((pr) => pr.id === proposalId)) return m;
              return {
                ...m,
                proposals: m.proposals.map((pr) => {
                  if (pr.id !== proposalId) return pr;
                  const currentApplied = Array.isArray(pr.appliedIndices) ? pr.appliedIndices : [];
                  const updatedIndices = currentApplied.includes(sceneIndex) ? currentApplied : [...currentApplied, sceneIndex];
                  const totalScenes = Array.isArray(pr.scenes) ? pr.scenes.length : 0;
                  const allApplied = totalScenes > 0 && updatedIndices.length >= totalScenes;
                  return {
                    ...pr,
                    appliedIndices: updatedIndices,
                    applied: allApplied || Boolean(pr.applied),
                    dismissed: false,
                  };
                }),
              };
            }),
          };
        }),
      };
    }

    case "PROPOSAL_DISMISSED": {
      const { projectId, proposalId, messageId } = p;
      return {
        ...state,
        threads: state.threads.map((th) => {
          if (th.projectId !== projectId) return th;
          return {
            ...th,
            messages: th.messages.map((m) => {
              if (messageId && m.id !== messageId) return m;
              if (!m.proposals || !m.proposals.some((pr) => pr.id === proposalId)) return m;
              return {
                ...m,
                proposals: m.proposals.map((pr) => {
                  if (pr.id !== proposalId) return pr;
                  return { ...pr, dismissed: true };
                }),
              };
            }),
          };
        }),
      };
    }

    case "PROPOSAL_REACTIVATED": {
      const { projectId, proposalId, messageId } = p;
      return {
        ...state,
        threads: state.threads.map((th) => {
          if (th.projectId !== projectId) return th;
          return {
            ...th,
            messages: th.messages.map((m) => {
              if (messageId && m.id !== messageId) return m;
              if (!m.proposals || !m.proposals.some((pr) => pr.id === proposalId)) return m;
              return {
                ...m,
                proposals: m.proposals.map((pr) => {
                  if (pr.id !== proposalId) return pr;
                  return { ...pr, dismissed: false };
                }),
              };
            }),
          };
        }),
      };
    }

    case "CONTINUITY_DECISION_SET": {
      const { projectId, observationId, status, note } = p;
      const projectDecisions = state.continuityDecisions?.[projectId] || {};
      const updated = {
        ...projectDecisions,
        [observationId]: {
          observationId,
          status, // 'reviewed' | 'dismissed' | 'pending'
          decidedAt: new Date().toISOString(),
          note: note || null,
        },
      };
      return {
        ...state,
        continuityDecisions: {
          ...(state.continuityDecisions || {}),
          [projectId]: updated,
        },
      };
    }

    case "CONTINUITY_PROPOSAL_APPLY": {
      const { projectId, observationId, sceneId, newPrompt } = p;
      const projectDecisions = state.continuityDecisions?.[projectId] || {};
      const updatedDecisions = {
        ...projectDecisions,
        [observationId]: {
          observationId,
          status: "applied",
          decidedAt: new Date().toISOString(),
          appliedToSceneId: sceneId,
        },
      };
      const updatedScenes = state.scenes.map((s) =>
        s.id === sceneId ? touch({ ...s, prompt: newPrompt }) : s
      );
      return {
        ...state,
        scenes: updatedScenes,
        continuityDecisions: {
          ...(state.continuityDecisions || {}),
          [projectId]: updatedDecisions,
        },
      };
    }

    case "SCRIPT_SAVE": {
      const { projectId, title, content, sourceMessageId, setActive = true } = p;
      if (!projectId || !content) return state;

      const projectScripts = (state.scripts || []).filter((s) => s.projectId === projectId);

      // Evitar duplicar copias al seleccionar otra vez el mismo mensaje sin cambios
      const existingMatch = projectScripts.find(
        (s) =>
          (sourceMessageId && s.sourceMessageId === sourceMessageId && s.content.trim() === content.trim()) ||
          (!sourceMessageId && s.content.trim() === content.trim())
      );

      let savedScript;
      let nextScripts;

      if (existingMatch) {
        savedScript = existingMatch;
        nextScripts = (state.scripts || []).map((s) => {
          if (s.id === existingMatch.id) {
            return {
              ...s,
              title: title || s.title,
              isActive: setActive ? true : s.isActive,
              updatedAt: new Date().toISOString(),
            };
          }
          if (setActive && s.projectId === projectId) {
            return { ...s, isActive: false };
          }
          return s;
        });
      } else {
        // Calcular siguiente versión v1, v2...
        let maxVer = 0;
        projectScripts.forEach((s) => {
          const m = (s.versionId || "").match(/^v(\d+)$/i);
          if (m) {
            const num = parseInt(m[1], 10);
            if (!isNaN(num) && num > maxVer) maxVer = num;
          }
        });
        const versionId = `v${maxVer + 1}`;

        savedScript = ProjectScript({
          projectId,
          versionId,
          title: title || `Borrador ${versionId}`,
          content: content.trim(),
          sourceMessageId: sourceMessageId || null,
          isActive: Boolean(setActive),
        });

        nextScripts = (state.scripts || []).map((s) =>
          setActive && s.projectId === projectId ? { ...s, isActive: false } : s
        );
        nextScripts.push(savedScript);
      }

      const nextProjects = state.projects.map((proj) => {
        if (proj.id === projectId) {
          return {
            ...proj,
            activeScriptId: setActive ? savedScript.id : proj.activeScriptId,
            updatedAt: new Date().toISOString(),
          };
        }
        return proj;
      });

      return {
        ...state,
        scripts: nextScripts,
        projects: nextProjects,
      };
    }

    case "SCRIPT_SET_ACTIVE": {
      const { projectId, scriptId } = p;
      if (!projectId) return state;

      const nextScripts = (state.scripts || []).map((s) => {
        if (s.projectId !== projectId) return s;
        return {
          ...s,
          isActive: s.id === scriptId,
          updatedAt: s.id === scriptId ? new Date().toISOString() : s.updatedAt,
        };
      });

      const nextProjects = state.projects.map((proj) =>
        proj.id === projectId
          ? { ...proj, activeScriptId: scriptId, updatedAt: new Date().toISOString() }
          : proj
      );

      return {
        ...state,
        scripts: nextScripts,
        projects: nextProjects,
      };
    }

    case "SCRIPT_UNSET_ACTIVE": {
      const { projectId } = p;
      if (!projectId) return state;

      const nextScripts = (state.scripts || []).map((s) => {
        if (s.projectId !== projectId) return s;
        return { ...s, isActive: false };
      });

      const nextProjects = state.projects.map((proj) =>
        proj.id === projectId
          ? { ...proj, activeScriptId: null, updatedAt: new Date().toISOString() }
          : proj
      );

      return {
        ...state,
        scripts: nextScripts,
        projects: nextProjects,
      };
    }

    case "SCRIPT_DELETE": {
      const { projectId, scriptId } = p;
      if (!projectId || !scriptId) return state;

      const nextScripts = (state.scripts || []).filter((s) => s.id !== scriptId);

      const targetProject = state.projects.find((pr) => pr.id === projectId);
      const wasActive = targetProject?.activeScriptId === scriptId;

      const nextProjects = state.projects.map((proj) => {
        if (proj.id === projectId && wasActive) {
          return { ...proj, activeScriptId: null, updatedAt: new Date().toISOString() };
        }
        return proj;
      });

      return {
        ...state,
        scripts: nextScripts,
        projects: nextProjects,
      };
    }

    case "SCRIPT_UPDATE": {
      const { projectId, scriptId, patch = {} } = p;
      if (!projectId || !scriptId) return state;

      const nextScripts = (state.scripts || []).map((s) => {
        if (s.id === scriptId && s.projectId === projectId) {
          return { ...s, ...patch, updatedAt: new Date().toISOString() };
        }
        return s;
      });

      return {
        ...state,
        scripts: nextScripts,
      };
    }

    case "SELECT": {
      const cur = state.selection[p.projectId] || { sceneId: null, assetId: null, nodeId: null, edgeId: null };
      return { ...state, selection: { ...state.selection, [p.projectId]: { ...cur, ...p.patch } } };
    }
    default:
      return state;
  }
}

// Mapea un job del backend al formato de GenerationJob del frontend
function _mapBackendJob(b) {
  const STATE_MAP = {
    not_sent: "pending",
    blocked: "blocked",
    uncertain: "uncertain",
    submitted: "running",
    polling: "running",
    completed: "done",
    failed: "failed",
    cancelled: "cancelled",
  };
  return {
    id: b.job_id,
    backendJobId: b.job_id,
    idempotencyKey: b.idempotency_key,
    status: STATE_MAP[b.submission_state] || "pending",
    submissionState: b.submission_state,
    providerId: b.provider_id,
    modelId: b.model_id,
    kind: b.kind || "image",
    prompt: b.prompt || "",
    referenceAssetIds: b.reference_asset_ids || [],
    resultAssetIds: (b.result_asset_ids && b.result_asset_ids.length > 0) ? b.result_asset_ids : (b.asset_id ? [b.asset_id] : []),
    resultAssetId: (b.result_asset_ids && b.result_asset_ids.length > 0) ? b.result_asset_ids[0] : (b.asset_id || null),
    externalTaskId: b.external_task_id,
    estimatedCostUsd: b.estimated_cost_usd,
    confirmedCostUsd: b.confirmed_cost_usd,
    errorDetail: b.error_detail,
    pollProgress: b.poll_progress,
    projectId: b.project_id || null,
    sceneId: b.scene_id || null,
    nodeId: b.node_id || null,
    createdAt: b.created_at,
    updatedAt: b.updated_at,
    completedAt: b.completed_at,
    isSimulation: b.is_simulation || false,
    params: b.params_sent || b.params || {},
    sourceAssetId: b.source_asset_id || b.sourceAssetId || null,
    endAssetId: b.end_asset_id || b.endAssetId || null,
    motionVideoAssetId: b.motion_video_asset_id || b.motionVideoAssetId || null,
    reserveUsd: b.reserve_usd ?? null,
  // ── Coste real y estimado ───────────────────────────────────────────────
  costReal: b.confirmed_cost_usd ?? null,      // coste confirmado por el proveedor
  costEstimate: b.estimated_cost_usd ?? null,  // estimación previa al envío
  model: b.model_id ?? null,                   // id del modelo usado
  };
}
